import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { clientAPI } from "@/services/api";
import {
  CheckCircle2,
  Clock,
  Download,
  RefreshCw,
  Search,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const safeText = (value) => String(value || "");

const ClientBRMs = () => {
  const [brms, setBRMs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchBRMs = async () => {
    setLoading(true);
    try {
      const res = await clientAPI.getBRMs();
      setBRMs(Array.isArray(res.data) ? res.data : res.data?.items || []);
    } catch {
      toast.error("Failed to load BRMs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBRMs();
  }, []);

  const filteredBRMs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return brms;

    return brms.filter((brm) =>
      [brm.name, brm.phone, brm.region, brm.status]
        .map(safeText)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [brms, search]);

  const summary = useMemo(() => {
    const activeUsers = brms.reduce((sum, brm) => sum + Number(brm.active_users || 0), 0);
    const pendingUsers = brms.reduce((sum, brm) => sum + Number(brm.pending_users || 0), 0);
    const totalEarnings = brms.reduce((sum, brm) => sum + Number(brm.total_earnings || 0), 0);
    const activeBRMs = brms.filter((brm) => brm.status === "active").length;

    return { activeUsers, pendingUsers, totalEarnings, activeBRMs };
  }, [brms]);

  const handleExport = () => {
    const csvContent = [
      ["Name", "Phone", "Region", "Active Users", "Pending Users", "Total Earnings", "Status"].join(","),
      ...brms.map((brm) => [
        brm.name,
        brm.phone,
        brm.region,
        brm.active_users,
        brm.pending_users,
        brm.total_earnings,
        brm.status,
      ].map((value) => `"${safeText(value).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brms-export-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  };

  const rows = filteredBRMs.map((brm) => ({
    id: brm.id || brm.phone,
    cells: [
      <div>
        <p className="font-semibold text-gray-900">{brm.name || "Unnamed BRM"}</p>
        <p className="mt-1 text-xs text-gray-500">{brm.phone || "No phone"}</p>
      </div>,
      <span className="font-medium text-gray-700">{brm.region || "Unassigned"}</span>,
      <span className="inline-flex items-center gap-1 font-medium text-[#039855]">
        <CheckCircle2 className="h-4 w-4" />
        {Number(brm.active_users || 0).toLocaleString()}
      </span>,
      <span className="inline-flex items-center gap-1 font-medium text-[#dc6803]">
        <Clock className="h-4 w-4" />
        {Number(brm.pending_users || 0).toLocaleString()}
      </span>,
      <span className="font-semibold text-gray-900">{formatCurrency(brm.total_earnings)}</span>,
      <TailAdminStatusBadge tone={brm.status === "active" ? "success" : "neutral"}>
        {brm.status || "unknown"}
      </TailAdminStatusBadge>,
    ],
  }));

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Operator network"
        title="BRM Performance"
        description="Track field operators, active users, pending activations, and earnings across your Proovam network."
        actions={
          <>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={loading || brms.length === 0}
              data-testid="export-brms-btn"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={fetchBRMs}
              disabled={loading}
              data-testid="refresh-brms-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard label="Total BRMs" value={brms.length} icon={Users} tone="primary" helper="In your network" loading={loading} />
        <TailAdminMetricCard label="Active BRMs" value={summary.activeBRMs} icon={TrendingUp} tone="success" helper="Currently active" loading={loading} />
        <TailAdminMetricCard label="Active users" value={summary.activeUsers.toLocaleString()} icon={CheckCircle2} tone="warning" helper="Verified subscribers" loading={loading} />
        <TailAdminMetricCard label="Total earnings" value={formatCurrency(summary.totalEarnings)} icon={Wallet} tone="neutral" helper="Commission paid" loading={loading} />
      </div>

      <TailAdminPanel
        title="BRM directory"
        description={`${filteredBRMs.length} of ${brms.length} BRMs shown`}
        action={
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by name, phone, or region..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 pl-9"
              data-testid="search-brms-input"
            />
          </div>
        }
      >
        {loading || filteredBRMs.length > 0 ? (
          <TailAdminDataTable
            loading={loading}
            columns={["BRM", "Region", "Active", "Pending", "Earnings", "Status"]}
            rows={rows}
          />
        ) : (
          <TailAdminEmptyState
            icon={Users}
            title={search ? "No BRMs match your search" : "No BRMs found"}
            description="BRMs will appear here once they start promoting your Prove-Requests."
          />
        )}
      </TailAdminPanel>
    </div>
  );
};

export default ClientBRMs;
